import React, { useCallback, useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { IconButton } from '@mui/material';
import { ChevronLeft, Copy, Play, Sparkles, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip, ConfirmationDialog, RouterLink } from 'src/components';
import { AlineButton } from 'src/components/generic';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { useIsOrgAdmin } from 'src/gql';
import { useDispatchSuccessSnack } from 'src/helpers';
import { useDeletePlaybook, useDuplicatePlaybook, useUpdatePlaybook } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { createEditPlaybookPrompt } from './edit-playbook-prompt';
import { PlaybookTextarea } from './PlaybookTextarea';
import { createRunPlaybookPrompt } from './run-playbook-prompt';
import {
  findStop,
  SuggestedTextDiff,
  SUGGESTION_CARD_TONES,
  SuggestionDecision,
  useStopDecision,
  useSuggestionReview,
} from './suggested-edits';
import type { SuggestionStop } from './suggested-edits';
import type { PlaybookDetail } from './types';
import {
  useDraftPromptInNewChat,
  useDraftPromptInOpenThread,
} from '../use-draft-prompt-in-new-chat';

/** The title's line height, which the actions beside it centre on. */
const TITLE_LINE_HEIGHT = 30;

const useStyles = makeStyles<{ current: boolean }>()((theme, { current }) => ({
  actionButton: {
    color: theme.appColors.secondaryType,
    flexShrink: 0,
  },
  /**
   * The actions, one title line tall so they centre on the first line of a
   * wrapped title and the rest of the title flows on beneath them.
   */
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    height: TITLE_LINE_HEIGHT,
  },
  backButton: {
    color: theme.appColors.iconsType,
  },
  /**
   * The title and the description, which is what a suggestion on the playbook
   * can touch. The current stop's treatment, the tint of an edited position
   * card under a 2px ring in its border colour, goes here rather than on the
   * header so the back chevron beside it stays outside the highlight. The ring
   * is an outline, which follows the corner radius and takes no layout space;
   * the transparent border and the negative margin with matching padding keep
   * the text where it sits when the highlight is off.
   */
  card: {
    backgroundColor: current ? SUGGESTION_CARD_TONES.change.background : 'transparent',
    border: '1px solid transparent',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    margin: theme.spacing(-0.5),
    minWidth: 0,
    outline: current ? `2px solid ${SUGGESTION_CARD_TONES.change.border}` : undefined,
    padding: theme.spacing(0.5),
  },
  description: {
    color: theme.appColors.secondaryType,
    fontSize: 14,
    width: '100%',
  },
  /**
   * The back button sits in its own column, so the title and the description
   * under it share one left edge. The chevron is aligned to the top so it
   * lines up with the title row, whatever the description adds below.
   */
  header: {
    alignItems: 'flex-start',
    columnGap: theme.spacing(0.5),
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
  },
  title: {
    flexGrow: 1,
    minWidth: 0,
  },
  // Pinned to the first line of a wrapped title diff; the offset centres it on that 30px line.
  titleDecision: {
    alignSelf: 'flex-start',
    marginTop: 3,
  },
  titleDiff: {
    color: theme.appColors.iconsType,
    display: 'block',
    fontSize: 22,
    fontWeight: 700,
    lineHeight: `${TITLE_LINE_HEIGHT}px`,
    overflowWrap: 'anywhere',
  },
  // Wraps rather than truncating, so a long name is read in full.
  titleDisplay: {
    color: theme.appColors.iconsType,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: `${TITLE_LINE_HEIGHT}px`,
    overflowWrap: 'anywhere',
  },
  /**
   * The title's pencil, pinned to the first line beside the actions rather
   * than centred on a wrapped title. The offset centres the 26px button on
   * the title line.
   */
  titleEditIcon: {
    marginTop: 2,
  },
  titleInput: {
    fontSize: 22,
    fontWeight: 700,
  },
  titlePlaceholder: {
    color: theme.appColors.secondaryType,
  },
  /**
   * Top-aligned so a wrapped title keeps the actions, or the pending
   * decision, on its first line.
   */
  titleRow: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
}));

/** The editable title, or its diff while a title change is pending: read-only until it resolves. */
const PlaybookTitle = React.memo(function PlaybookTitle({
  disabled,
  playbook,
  suggestedTitle,
}: {
  disabled: boolean;
  playbook: PlaybookDetail;
  suggestedTitle: string | undefined;
}): JSX.Element {
  const { classes } = useStyles({ current: false });
  const { updatePlaybook } = useUpdatePlaybook();
  const handleSave = useCallback(
    (title: string) => updatePlaybook({ playbookId: playbook.id, props: { title } }),
    [playbook.id, updatePlaybook]
  );

  if (suggestedTitle !== undefined) {
    return (
      <SuggestedTextDiff
        after={suggestedTitle}
        before={playbook.title}
        className={classes.titleDiff}
      />
    );
  }

  return (
    <InlineEditField
      multiline
      classes={{
        display: classes.titleDisplay,
        icon: classes.titleEditIcon,
        input: classes.titleInput,
        placeholder: classes.titlePlaceholder,
      }}
      disabled={disabled}
      editTooltip="Rename playbook"
      iconAlign="start"
      iconSize={16}
      /**
       * An untitled playbook (typically one just created) opens ready to
       * be named. Mount-only: the header mounts once the playbook has loaded.
       */
      initiallyEditing={!playbook.title}
      placeholder="Type a playbook title..."
      textPlaceholder="Untitled Playbook"
      value={playbook.title}
      onSave={handleSave}
    />
  );
});

/**
 * While a suggestion is pending on the header the accept/reject controls take
 * the place of the playbook actions; the suggestion is the only thing to
 * decide until it resolves.
 */
const PlaybookHeaderActions = React.memo(function PlaybookHeaderActions({
  disabled,
  patchStop,
  playbook,
}: {
  disabled: boolean;
  patchStop: SuggestionStop | null;
  playbook: PlaybookDetail;
}): JSX.Element {
  const { classes } = useStyles({ current: false });
  const draftPromptInNewChat = useDraftPromptInNewChat();
  const decision = useStopDecision(patchStop);
  const handleRun = useCallback(() => {
    draftPromptInNewChat(
      createRunPlaybookPrompt({ playbookId: playbook.id, playbookTitle: playbook.title })
    );
  }, [draftPromptInNewChat, playbook.id, playbook.title]);

  if (patchStop) {
    return (
      <SuggestionDecision
        acceptDisabledReason={patchStop.blockedReason}
        className={classes.titleDecision}
        kind="change"
        rationale={patchStop.rationale}
        onAccept={decision.onAccept}
        onReject={decision.onReject}
        onSelect={decision.onSelect}
      />
    );
  }

  return (
    <div className={classes.actions}>
      <EditPlaybookWithAiButton disabled={disabled} playbook={playbook} />
      <DuplicatePlaybookButton disabled={disabled} playbookId={playbook.id} />
      <DeletePlaybookButton disabled={disabled} playbook={playbook} />
      <AlineButton
        color="primary"
        disabled={disabled}
        size="small"
        startIcon={<Play size={14} />}
        variant="contained"
        onClick={handleRun}
      >
        Run
      </AlineButton>
    </div>
  );
});

/** The editable description, or its diff while a description change is pending. */
const PlaybookDescription = React.memo(function PlaybookDescription({
  disabled,
  playbook,
  suggestedDescription,
}: {
  disabled: boolean;
  playbook: PlaybookDetail;
  suggestedDescription: string | undefined;
}): JSX.Element {
  const { classes } = useStyles({ current: false });
  const { updatePlaybook } = useUpdatePlaybook();
  const handlePersist = useCallback(
    (description: string) => updatePlaybook({ playbookId: playbook.id, props: { description } }),
    [playbook.id, updatePlaybook]
  );

  if (suggestedDescription !== undefined) {
    return (
      <SuggestedTextDiff
        after={suggestedDescription}
        before={playbook.description}
        className={classes.description}
        emptyLabel="No description"
      />
    );
  }

  return (
    <PlaybookTextarea
      className={classes.description}
      dataCy={Select.playbooks.description}
      disabled={disabled}
      placeholder="Type a playbook description..."
      value={playbook.description}
      onPersist={handlePersist}
    />
  );
});

interface PlaybookHeaderProps {
  playbook: PlaybookDetail;
  disabled?: boolean;
}

export function PlaybookHeader({ disabled = false, playbook }: PlaybookHeaderProps): JSX.Element {
  const { currentStop, model } = useSuggestionReview();
  const patchStop = findStop(model, playbook.id, 'patch');
  const patch = model.playbookEdit?.patch ?? null;
  const isCurrent = Boolean(patchStop && currentStop?.key === patchStop.key);
  const { classes } = useStyles({ current: isCurrent });

  return (
    <div className={classes.header}>
      <IconButton
        className={classes.backButton}
        component={RouterLink}
        data-cy={Select.playbooks.detailBack}
        size="small"
        to={link.PlaybooksIndex()}
      >
        <ChevronLeft size={20} />
      </IconButton>
      <div className={classes.card}>
        <div className={classes.titleRow}>
          <div className={classes.title} data-cy={Select.playbooks.detailTitle}>
            <PlaybookTitle
              disabled={disabled}
              playbook={playbook}
              suggestedTitle={patch?.value.title}
            />
          </div>
          <PlaybookHeaderActions disabled={disabled} patchStop={patchStop} playbook={playbook} />
        </div>
        <PlaybookDescription
          disabled={disabled}
          playbook={playbook}
          suggestedDescription={patch?.value.description}
        />
      </div>
    </div>
  );
}

/**
 * Drafts an edit request for this playbook into the open thread, or a new
 * chat when none is open. The user finishes the sentence and sends it; the
 * planner routes it to the playbook editor, whose edits come back as
 * suggestions on this page.
 */
function EditPlaybookWithAiButton({
  disabled,
  playbook,
}: {
  disabled: boolean;
  playbook: PlaybookDetail;
}): JSX.Element {
  const { classes } = useStyles({ current: false });
  const draftPrompt = useDraftPromptInOpenThread();
  const handleEditWithAi = useCallback(() => {
    draftPrompt(
      createEditPlaybookPrompt({ playbookId: playbook.id, playbookTitle: playbook.title })
    );
  }, [draftPrompt, playbook.id, playbook.title]);

  return (
    <AlineTooltip arrow placement="top" title="Edit playbook with AI">
      <IconButton
        aria-label="Edit playbook with AI"
        className={classes.actionButton}
        data-cy={Select.playbooks.editWithAiButton}
        disabled={disabled}
        size="small"
        onClick={handleEditWithAi}
      >
        <Sparkles size={16} />
      </IconButton>
    </AlineTooltip>
  );
}

function DuplicatePlaybookButton({
  disabled,
  playbookId,
}: {
  disabled: boolean;
  playbookId: string;
}): JSX.Element {
  const { classes } = useStyles({ current: false });
  const navigate = useNavigate();
  const { duplicatePlaybook, loading } = useDuplicatePlaybook();
  const dispatchDuplicatedSnack = useDispatchSuccessSnack({ message: 'Playbook duplicated.' });
  const handleDuplicate = useCallback(async () => {
    const { data } = await duplicatePlaybook(playbookId);
    if (!data) return;

    dispatchDuplicatedSnack();
    navigate(link.PlaybooksDetail({ playbookId: data.duplicatePlaybook.id }));
  }, [dispatchDuplicatedSnack, duplicatePlaybook, navigate, playbookId]);

  return (
    <AlineTooltip arrow placement="top" title="Duplicate playbook">
      <IconButton
        aria-label="Duplicate playbook"
        className={classes.actionButton}
        data-cy={Select.playbooks.duplicateButton}
        disabled={disabled || loading}
        size="small"
        onClick={handleDuplicate}
      >
        <Copy size={16} />
      </IconButton>
    </AlineTooltip>
  );
}

function DeletePlaybookButton({
  disabled,
  playbook,
}: {
  disabled: boolean;
  playbook: PlaybookDetail;
}): JSX.Element {
  const { classes } = useStyles({ current: false });
  const navigate = useNavigate();
  const { deletePlaybook } = useDeletePlaybook();
  const { currentUser, isOrgAdmin } = useIsOrgAdmin();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dispatchDeletedSnack = useDispatchSuccessSnack({ message: 'Playbook deleted.' });
  const canDelete = isOrgAdmin || currentUser?.id === playbook.createdBy?.id;
  const openDeleteConfirm = useCallback(() => setConfirmOpen(true), []);
  const handleDeleteConfirmed = useCallback(async () => {
    const { data } = await deletePlaybook(playbook.id);
    if (!data) return;

    dispatchDeletedSnack();
    navigate(link.PlaybooksIndex());
  }, [deletePlaybook, dispatchDeletedSnack, navigate, playbook.id]);

  return (
    <>
      <AlineTooltip
        arrow
        placement="top"
        title={canDelete ? 'Delete playbook' : 'Only the playbook author or an admin can delete it'}
      >
        {/* Wrapped so the tooltip still works while the button is disabled. */}
        <span>
          <IconButton
            aria-label="Delete playbook"
            className={classes.actionButton}
            data-cy={Select.playbooks.detailDelete}
            disabled={disabled || !canDelete}
            size="small"
            onClick={openDeleteConfirm}
          >
            <Trash2 size={16} />
          </IconButton>
        </span>
      </AlineTooltip>
      <ConfirmationDialog
        message="Are you sure you want to delete this playbook? This action cannot be undone."
        open={confirmOpen}
        title="Delete playbook?"
        onClickPrimary={handleDeleteConfirmed}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
